import type { AcceptedWarning, PreVerificationPhase } from "@/api/onboarding-api.types"
import { useCallback, useMemo } from "react"
import { Loader2 } from "lucide-react"
import { AuthProvider } from "./contexts/auth-context"
import { CodeProvider } from "./contexts/code-context"
import { LanguageProvider } from "./contexts/language-context"
import { useTranslation } from "./hooks/use-translation"
import { Button } from "./components/ui/button"
import { useCodeValues } from "./hooks/use-code-values"
import { usePlacements } from "./hooks/use-placements"
import { PlacementDetailsContent } from "./components/ats-fbo/placement-details-content"
import { PlacementDetailsDrawer } from "./components/ats-fbo/placement-details-drawer"
import { DrawerShell } from "./components/ats-fbo/drawer-shell"
import type { PlacementFormState } from "./components/ats-fbo/types"
import { env } from "./lib/env"

// ---------------------------------------------------------------------------
// Loading/error shell — always shows X and Cancel so user can escape
// ---------------------------------------------------------------------------
function LoadingShell({ title, sourceName, onClose, error }: {
  title?: string
  sourceName?: string
  onClose: () => void
  error?: string
}) {
  const { t } = useTranslation()
  return (
    <DrawerShell
      title={title}
      sourceName={sourceName}
      onClose={onClose}
      footer={
        <div className="flex items-center justify-end gap-3">
          <Button variant="outline" onClick={onClose} className="bg-transparent">{t("cancel")}</Button>
          {!error && <div className="h-9 min-w-[160px] bg-gray-100 rounded animate-pulse" />}
        </div>
      }
    >
      <div className="h-full flex items-center justify-center">
        {error
          ? <p className="text-sm text-red-500">{error}</p>
          : <Loader2 size={20} className="animate-spin text-gray-400" />}
      </div>
    </DrawerShell>
  )
}

function AppContent() {
  const { t } = useTranslation()
  const { isReady: isCodeValuesReady, getCodeValues, getCodeValueCode, getCodeValueIdByCode } = useCodeValues()

  const params = useMemo(() => new URLSearchParams(window.location.search), [])
  const onboardingId = params.get("id")

  const {
    placement,
    idDisplayText,
    recordNoun,
    title,
    sourceName,
    isLoading,
    loadError,
    profileTypes,
    lobs,
    currencies,
    submitPlacement,
    preflightPlacement,
    refreshPreVerification,
    refreshSource,
  } = usePlacements({
    onboardingId,
    // Consumers set these when iframing: ?idDisplayText=ATS-12345&sourceName=ATS+queue&recordNoun=placement&title=Placement+Details
    // Omitting any lets the hook derive them; idDisplayText falls back to "{abbreviated sourceType}-{sourceId}",
    // sourceName falls back to the API's sourceType, recordNoun falls back to sourceName, and title falls back to "Capitalize(recordNoun) Details".
    idDisplayText: params.get("idDisplayText"),
    sourceName: params.get("sourceName"),
    recordNoun: params.get("recordNoun"),
    title: params.get("title"),
    isCodeValuesReady,
    getCodeValues,
    getCodeValueCode,
    getCodeValueIdByCode,
  })

  // ---------------------------------------------------------------------------
  // postMessage communication with parent frame
  // ---------------------------------------------------------------------------
  const handleClose = useCallback(() => {
    window.parent.postMessage({ type: "onboarding:close" }, "*")
  }, [])

  const handleSubmit = useCallback(async (form: PlacementFormState, acceptedWarnings: AcceptedWarning[]) => {
    const result = await submitPlacement(form, acceptedWarnings)
    if (result.ok && result.data.success) {
      window.parent.postMessage({ type: "onboarding:submitted", result: result.data }, "*")
    }
    return result
  }, [submitPlacement])

  const handleViewPreVerification = useCallback((requestId: string, phase: PreVerificationPhase) => {
    window.parent.postMessage({ type: "onboarding:view-pre-verification", requestId, phase }, "*")
  }, [])

  // ---------------------------------------------------------------------------
  // Render — always wrap in drawer for preview mode so shell states don't
  // escape the panel. Use || undefined so empty strings fall back to skeleton.
  // ---------------------------------------------------------------------------
  let inner: React.ReactNode

  if (!onboardingId) {
    inner = <LoadingShell title={title || undefined} sourceName={sourceName || undefined} onClose={handleClose} error={t("noOnboardingId")} />
  } else if (loadError) {
    inner = <LoadingShell title={title || undefined} sourceName={sourceName || undefined} onClose={handleClose} error={loadError} />
  } else if (isLoading || !placement) {
    inner = <LoadingShell title={title || undefined} sourceName={sourceName || undefined} onClose={handleClose} />
  } else {
    inner = (
      <PlacementDetailsContent
        placement={placement}
        idDisplayText={idDisplayText}
        recordNoun={recordNoun}
        title={title}
        sourceName={sourceName}
        profileTypes={profileTypes}
        lobs={lobs}
        currencies={currencies}
        onClose={handleClose}
        onSubmit={handleSubmit}
        onPreflight={preflightPlacement}
        onRefreshPreVerification={refreshPreVerification}
        onViewPreVerification={handleViewPreVerification}
        onRefreshSource={refreshSource}
      />
    )
  }

  if (env.previewDrawer) {
    return <PlacementDetailsDrawer onClose={handleClose}>{inner}</PlacementDetailsDrawer>
  }
  return inner
}

export default function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <CodeProvider>
          <AppContent />
        </CodeProvider>
      </LanguageProvider>
    </AuthProvider>
  )
}
